import React, { useCallback, useEffect, useState } from 'react';
import Topbar from '@/components/layout/Topbar';
import CandidateCard from '@/components/CandidateCard';
import { api } from '@/lib/api';
import { RefreshCw, Sparkles, AlertOctagon } from 'lucide-react';
import { Link } from 'react-router-dom';

const fmt = (n, d = 2) => (n === null || n === undefined || isNaN(n) ? '—' : Number(n).toFixed(d));

function motivoLegible(adv) {
  if (!adv) return null;
  if (adv.startsWith('binance_todos_los_hosts_fallaron')) return 'Binance no responde en ninguno de sus endpoints públicos.';
  if (adv.startsWith('binance_unreachable')) return `Fallo al conectar con Binance (${adv.split(':').slice(1).join(':') || 'sin detalle'}).`;
  if (adv.startsWith('datos_incompletos')) return `Datos incompletos para ${adv.split(':')[1] || 'un par'}.`;
  if (adv === 'rss_todos_los_feeds_fallaron') return 'Todos los feeds RSS de noticias cayeron: A1 se quedaría sin titulares.';
  if (adv.startsWith('rss_feed_caido')) return null; // menor, no bloquea si al menos uno responde
  return adv;
}

export default function Dashboard() {
  const [loading, setLoading] = useState(true);
  const [briefing, setBriefing] = useState(null);
  const [candidatas, setCandidatas] = useState([]);
  const [status, setStatus] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data: b }, { data: s }] = await Promise.all([
        api.get('/briefing/latest'),
        api.get('/status'),
      ]);
      setBriefing(b.briefing);
      setCandidatas(b.candidatas || []);
      setStatus(s);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const esperando = candidatas.filter((c) => c.estado === 'esperando_jorge');
  const otros = candidatas.filter((c) => c.estado !== 'esperando_jorge');

  return (
    <>
      <Topbar
        title="Briefing del día"
        subtitle={briefing ? `snapshot ${briefing.snapshot_id} · ${briefing.trading_day} · sesgo ${briefing.sesgo_consolidado}` : 'sin briefing aún'}
        right={
          <button onClick={load} className="inline-flex items-center gap-2 rounded-md px-3 py-1.5 text-xs border border-border hover:bg-muted" data-testid="btn-refresh-briefing">
            <RefreshCw size={14} /> Refrescar
          </button>
        }
      />
      <div className="px-3 sm:px-4 lg:px-6 py-4 grid grid-cols-1 lg:grid-cols-12 gap-4">
        {status && status.snapshot_valido === false && (
          <div
            className="lg:col-span-12 panel alarm-critical alarm-pulse border rounded-lg p-4 flex items-start gap-3"
            role="alert"
            data-testid="banner-r14-invalido"
          >
            <AlertOctagon size={22} className="mt-0.5 shrink-0" />
            <div className="flex-1">
              <div className="font-display font-semibold text-base mb-1">
                R14 · Datos de mercado no disponibles — hoy no se opera
              </div>
              <div className="text-sm mb-2">
                El último snapshot fue rechazado porque el sistema no pudo garantizar la calidad de los datos que
                alimentan al pipeline. Ninguna candidata será ejecutada hasta que R14 vuelva a estar en verde.
              </div>
              {status.snapshot_motivos && status.snapshot_motivos.length > 0 && (
                <ul className="text-xs list-disc pl-5 space-y-0.5" data-testid="banner-r14-motivos">
                  {status.snapshot_motivos
                    .map((a) => motivoLegible(a))
                    .filter(Boolean)
                    .slice(0, 5)
                    .map((m, i) => <li key={i}>{m}</li>)}
                </ul>
              )}
              <div className="text-[11px] mt-2 opacity-80">
                Registrado: <span className="font-num">{status.snapshot_check_ts || '—'}</span>
              </div>
            </div>
          </div>
        )}
        <section className="lg:col-span-8 space-y-3">
          {!briefing && !loading && (
            <div className="panel p-6 text-center" data-testid="empty-briefing">
              <Sparkles className="mx-auto mb-2" />
              <h3 className="text-base mb-1">Aún no hay briefing</h3>
              <p className="text-sm text-muted-foreground mb-3">Dispara el pipeline para generar candidatas del día.</p>
              <Link to="/ejecutar" className="inline-flex items-center gap-2 rounded-md px-3 py-2 text-sm text-white" style={{ background: 'hsl(var(--brand))' }}>Correr pipeline ahora</Link>
            </div>
          )}
          {loading && <div className="panel p-6 text-sm text-muted-foreground" data-testid="loading">Cargando briefing...</div>}
          {briefing && (
            <div className="panel p-4">
              <div className="text-xs text-muted-foreground mb-1">Resumen del mercado</div>
              <p className="text-sm whitespace-pre-line">{briefing.resumen_mercado}</p>
            </div>
          )}

          {esperando.length > 0 && (
            <div className="space-y-2">
              <h2 className="text-sm text-muted-foreground uppercase tracking-wide">Esperando decisión de Jorge</h2>
              {esperando.map((c) => <CandidateCard key={c.candidata_id} candidata={c} onChanged={load} />)}
            </div>
          )}
          {otros.length > 0 && (
            <div className="space-y-2 pt-2">
              <h2 className="text-sm text-muted-foreground uppercase tracking-wide">Otras candidatas</h2>
              {otros.map((c) => <CandidateCard key={c.candidata_id} candidata={c} onChanged={load} />)}
            </div>
          )}
        </section>

        <aside className="lg:col-span-4 space-y-3">
          {status && (
            <>
              <div className="panel p-4">
                <div className="text-xs text-muted-foreground mb-1">Equity simulado</div>
                <div className="font-mono-num text-2xl">{fmt(status.equity_usdt)} <span className="text-xs text-muted-foreground">USDT</span></div>
                <div className="mt-2 flex items-center gap-3 text-xs">
                  <span>PnL día: <span className={`font-mono-num ${status.pnl_dia_usdt > 0 ? 'pnl-pos' : status.pnl_dia_usdt < 0 ? 'pnl-neg' : 'pnl-neutral'}`}>{fmt(status.pnl_dia_usdt)}</span></span>
                  <span>Posiciones: <span className="font-mono-num">{status.posiciones_abiertas}</span></span>
                </div>
              </div>
              <div className="panel p-4">
                <div className="text-xs text-muted-foreground mb-2">Estado operativo</div>
                <div className="space-y-1.5 text-xs">
                  <div className="flex justify-between"><span>Ventana operativa</span><span className={status.ventana_activa ? 'pnl-pos' : 'pnl-neg'}>{status.ventana_activa ? 'activa' : 'cerrada'}</span></div>
                  <div className="flex justify-between"><span>Día verde</span><span className={status.dia_verde ? 'pnl-pos' : 'pnl-neutral'}>{status.dia_verde ? 'sí' : 'no'}</span></div>
                  <div className="flex justify-between"><span>Kill switch día</span><span className={status.kill_switch_activo ? 'pnl-neg' : 'pnl-neutral'}>{status.kill_switch_activo ? 'activo' : 'off'}</span></div>
                </div>
              </div>
              <div className="panel p-4">
                <div className="text-xs text-muted-foreground mb-2">Reglas R (top 5)</div>
                <ul className="space-y-1 text-xs">
                  {status.reglas.slice(0, 5).map((r) => (
                    <li key={r.codigo} className="flex justify-between">
                      <span>{r.codigo}</span>
                      <span className={r.estado === 'ok' ? 'pnl-pos' : 'pnl-neg'}>{r.estado}</span>
                    </li>
                  ))}
                </ul>
                <Link to="/estado" className="text-[11px] text-muted-foreground underline mt-2 inline-block">ver todas</Link>
              </div>
            </>
          )}
        </aside>
      </div>
    </>
  );
}
